<template>
  <v-container mt-7>
    <v-form ref="form" lazy-validation>
      <v-row>
        <v-col class="selectorColumn">
          <v-select
            v-model="orgChoice"
            :items="organizations"
            label="Organizations"
            :disabled="orgDisabled"
            outlined
            dense
            clearable
            @click:clear="$nextTick(() => (orgChoice = null))"
          >
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="insChoice"
            :items="insurances"
            label="Select Insurance"
            :disabled="insDisabled"
            clearable
            outlined
            dense
            required
            :rules="[() => !!insChoice || 'Please select an insurance']"
            @click:clear="$nextTick(() => (insChoice = null))"
          ></v-select>
        </v-col>
        <v-col v-if="reportChoice === 'Comparative KPI'" class="selectorColumn">
          <v-select
            v-model="groupChoice"
            :items="groups"
            label="Groups"
            :disabled="groupDisabled"
            multiple
            outlined
            dense
            required
            :rules="[(groupChoice) => groupChoice.length !== 0 || 'Please select a Group']"
            clearable
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ groupChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col v-else class="selectorColumn">
          <v-select
            v-model="groupChoice"
            :items="groups"
            label="Groups"
            :disabled="groupDisabled"
            multiple
            outlined
            dense
            clearable
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ groupChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="provChoice"
            :items="providers"
            item-text="Provide_Name"
            return-object
            label="Providers"
            :disabled="provDisabled"
            multiple
            outlined
            dense
            clearable
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item.Provide_Name }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ provChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="locChoice"
            :items="locations"
            item-text="Center_Location"
            return-object
            label="Locations"
            :disabled="locDisabled"
            multiple
            outlined
            dense
            clearable
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item }}</div>
              <span v-if="index === 1" class="grey--text caption">+{{ locChoice.length - 1 }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="reportChoice"
            :items="reports"
            :menu-props="{ maxHeight: '400' }"
            label="Select a View"
            clearable
            required
            outlined
            dense
            :rules="[() => !!reportChoice || 'Please select a report']"
            @click:clear="$nextTick(() => (reportChoice = null))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                label="Start Date"
                append-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              type="month"
              @input="menu = false"
              :max="maxEndDate"
              show-current="false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                append-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              type="month"
              :max="getAllowedDates"
              show-current="false"
              @input="menu2 = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-btn @click="validate" color="secondary" depressed block class="py-5">
            Run Report
            <v-icon small>mdi-play</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-row>
      <v-col v-if="load" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col>
      <v-col
        v-else-if="(!insChoice || !reportChoice) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Please select all required fields </v-alert>
      </v-col>
      <v-col v-else-if="(!!insChoice || !!reportChoice) && !hasRun" cols="12">
        <v-alert :value="true" type="info"> Press run report to continue </v-alert>
      </v-col>
      <v-col v-else-if="showReport && changeDetected && hasRun" cols="12">
        <v-alert :value="true" type="warning">
          Change detected: Run the report to see updated data
        </v-alert>
      </v-col>
      <v-col v-else-if="reportData.length === 0 && showReport && hasRun" cols="12">
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter selections and
          run report again
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <kpi
            v-if="this.reportChoice == 'Group KPI'"
            :organization="orgChoice"
            :group="groupChoice"
            :groupNum="groupNum"
            :providers="provChoice"
            :locations="locChoice"
            :insurance="selectedInsurance"
            :startDate="startDate"
            :endDate="endDate"
            :currentTab="tab"
            :hideToolbar="false"
            :xlColWidth="3"
            :kpiData="reportData"
          />
          <comp-kpi
            v-if="this.reportChoice == 'Comparative KPI'"
            :organization="orgChoice"
            :group="groupChoice"
            :groupObject="groupObject"
            :groupNum="groupNum"
            :providers="provChoice"
            :locations="locChoice"
            :insurance="selectedInsurance"
            :startDate="startDate"
            :endDate="endDate"
            :currentTab="tab"
            :data="reportData"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import axios from 'axios';
import Kpi from '../components/financeComponents/Kpi';
import CompKpi from '../components/financeComponents/CompKpi';

export default {
  components: {
    Kpi,
    CompKpi,
  },
  data() {
    return {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3)
        .toISOString()
        .substr(0, 7),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      maxEndDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      menu: false,
      modal: false,
      menu2: false,
      tab: null,
      reportChoice: null,
      reports: ['Group KPI', 'Comparative KPI'],
      orgChoice: null,
      groupChoice: [],
      provChoice: [],
      provNums: [],
      locChoice: [],
      insChoice: null,
      user: '',
      organizations: [],
      providers: [],
      groups: [],
      groupObject: [],
      groupNum: [],
      insurances: [],
      locations: [],
      reportData: [],
      hasRun: false,
      showReport: false,
      changeDetected: false,
      load: false,
      selectedInsurance: null,
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    userInsuranceFirst() {
      const ins = this.user.insurance;
      if (ins == null) return null;
      return Array.isArray(ins) ? ins[0] : ins;
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.getPatients();
      }
    },
    async getPatients() {
      this.selectedInsurance = this.insChoice;
      this.load = true;
      let url;

      // clear report data
      this.membership = [];

      // This will grab the correct groupNums if no groups are selected. This is for a group level login when only select groups available to the user
      if (this.user.role === 'Group' && this.groupNum.length === 0) {
        // Check for pending request and if so cancel old one for new incoming
        this.cancelSearch();
        // Set the CancelToken
        this.cancelSource = axios.CancelToken.source();

        const response = await axios
          .get(`api/selection/${this.insChoice}/group/${this.groups}`, {
            cancelToken: this.cancelSource.token,
          })
          .then((response) => {
            const { data } = response;
            data.forEach((element) => {
              if (this.insChoice) {
                if (this.groupNum.indexOf(element.Center_PCP_Number) === -1) {
                  this.groupNum.push(element.Center_PCP_Number);
                }
              }
            });
            this.cancelSource = null;
          });
      }

      // set API route
      if (this.reportChoice === 'Group KPI') {
        url = `api/kpi/groups=${this.groupNum}/providers=${this.provNums}/locs=${this.locChoice}/${this.insChoice}/${this.startDate}/${this.endDate}`;
      } else if (this.reportChoice === 'Comparative KPI') {
        url = `api/compkpi/location/group=${this.groupChoice}/groupNum=${this.groupNum}/insurance=${this.insChoice}/provider=${this.provNums}/location=${this.locChoice}/start=${this.startDate}/end=${this.endDate}`;
      }

      // Check for pending request and if so cancel old one for new incoming
      this.cancelSearch();
      // Set the CancelToken
      this.cancelSource = axios.CancelToken.source();

      const response = axios.get(url, { cancelToken: this.cancelSource.token }).then((response) => {
        this.showReport = true;
        // Set cancelsource back to null
        this.cancelSource = null;
        this.reportData = response.data;
        this.load = false;
        this.changeDetected = false;
        this.hasRun = true;
      });
    },
    async checkPresets() {
      const r = this.user && this.user.role ? String(this.user.role).toLowerCase() : '';
      if (r === 'provider' || r === 'group') {
        await this.setPresets();
      } else if (r === 'clinical') {
        this.$router.push('/membership');
      } else {
        this.getOrgs();
        this.getInsurances();
      }
    },
    async setPresets() {
      const r = this.user && this.user.role ? String(this.user.role).toLowerCase() : '';
      this.organizations = this.user.organization;
      this.groups = this.user.group;
      const ins = this.user.insurance;
      this.insurances = Array.isArray(ins) ? ins : ins != null && ins !== '' ? [ins] : [];
      if (r === 'provider') {
        this.insChoice = this.userInsuranceFirst();
        this.orgChoice = this.user.organization;
        this.groupChoice = this.user.group;
        this.providers = this.user.provider;
        this.location = this.user.location;
      } else if (r === 'group') {
        this.orgChoice = this.user.organization;
        this.insChoice = this.userInsuranceFirst();
      } else if (r === 'clinical') {
        this.$router.push('/');
      }
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
        })
        .catch((err) => this.$router.push('/login'));
      await this.checkPresets();
      if (this.insChoice) {
        await this.getGroups();
      }
    },

    async getOrgs() {
      // const response = await axios.get('api/list/organization').then((response) => {
      //   this.organizations = response.data;
      //   this.orgChoice = this.organizations[0];
      // });
      this.organizations = ['Clearspring', 'Humana'];
    },
    async getInsurances() {
      // const response = await axios.get('api/list/insurances').then((response) => {
      //   this.insurances = response.data;
      // });
      this.insurances = ['Clearspring', 'Humana'];
    },
    groupNameFromListItem(grp) {
      if (grp == null) return '';
      if (typeof grp === 'string') return String(grp).trim();
      let raw = grp.GROUP_NAME;
      if (raw == null || raw === '') raw = grp.group_name;
      if (raw == null || raw === '') raw = grp.Group_Name;
      if (raw == null || raw === '') {
        const bpi = grp.BILLING_PROVIDER_NPI;
        if (bpi != null && bpi !== '') {
          raw = Array.isArray(bpi) ? bpi.map(String).join(',') : String(bpi);
        }
      }
      if (raw == null || raw === '') raw = grp._id;
      if (raw == null || raw === '') return '';
      return String(raw).trim();
    },
    /** Nomes de grupo gravados no perfil (strings ou objetos). */
    groupsFromUserProfile() {
      if (!this.user || this.user.group == null) return [];
      const raw = this.user.group;
      const arr = Array.isArray(raw) ? raw : [raw];
      const out = [];
      arr.forEach((g) => {
        if (g == null) return;
        const name = typeof g === 'string' ? String(g).trim() : this.groupNameFromListItem(g);
        if (name) out.push(name);
      });
      return out;
    },
    normalizeGroupList(responseData) {
      const rows = Array.isArray(responseData) ? responseData : [];
      return rows
        .map((g) => this.groupNameFromListItem(g))
        .filter((name) => name && String(name).trim() !== '');
    },
    /** Fallback quando o perfil ainda não está em `this.user` (corrida com o primeiro clique). */
    async getGroupsByInsuranceSlugOnly() {
      const insSlug = encodeURIComponent(String(this.insChoice));
      const response = await axios.get(`api/list/${insSlug}/groups`);
      this.groups = this.normalizeGroupList(response.data);
    },
    /**
     * Membership: Group/Affiliation → ipaId (ou npi); Admin/Manager → /:insurance/groups.
     * Se ipaId/devolver vazio ou não houver NPI, faz fallback para lista por seguro e para user.group.
     */
    async getGroups() {
      if (!this.insChoice) {
        this.groups = [];
        return;
      }

      const insSlug = encodeURIComponent(String(this.insChoice));

      if (!this.user || typeof this.user !== 'object' || !this.user.role) {
        try {
          await this.getGroupsByInsuranceSlugOnly();
        } catch (e) {
          console.error('[KPIs getGroups] insurance-only (no user yet)', e);
          this.groups = [];
        }
        return;
      }

      const r = String(this.user.role).toLowerCase();
      const npi = this.user.npi != null && this.user.npi !== '' ? String(this.user.npi) : '';

      const mergeInsuranceAndProfile = async (namesSoFar) => {
        let names = namesSoFar && namesSoFar.length ? namesSoFar.slice() : [];
        if (names.length === 0) {
          try {
            const res = await axios.get(`api/list/${insSlug}/groups`);
            names = this.normalizeGroupList(res.data);
          } catch (e) {
            names = [];
          }
        }
        if (names.length === 0) {
          names = this.groupsFromUserProfile();
        }
        this.groups = names;
      };

      try {
        if (
          ((r === 'group' && Number(this.user.npi) == this.user.npi) || r === 'provider') &&
          this.insurances.length === 1 &&
          npi
        ) {
          const response = await axios.get(`api/list/npi/${encodeURIComponent(npi)}/groups`);
          await mergeInsuranceAndProfile(this.normalizeGroupList(response.data));
          return;
        }

        if (r === 'affiliation' || r === 'group') {
          let fromIpa = [];
          if (npi) {
            try {
              const response = await axios.get(
                `api/list/ipaId/${encodeURIComponent(npi)}/groups`
              );
              fromIpa = this.normalizeGroupList(response.data);
            } catch (e) {
              console.warn('[KPIs getGroups] ipaId', e);
            }
          }
          await mergeInsuranceAndProfile(fromIpa);
          return;
        }

        const response = await axios.get(`api/list/${insSlug}/groups`);
        await mergeInsuranceAndProfile(this.normalizeGroupList(response.data));
      } catch (e) {
        console.error('[KPIs getGroups]', e);
        await mergeInsuranceAndProfile([]);
      }
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
  },
  watch: {
    async reportChoice() {
      this.showReport = false;
      this.changeDetected = true;
      this.reportData = [];
      // Comparative KPI e Group KPI partilham o mesmo `groups`; garantir lista após mudar vista.
      if (this.insChoice) {
        await this.getGroups();
      }
    },
    async groupChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }

      // Populates the group selector for group level logins with one insurance and one group available
      if (
        this.user &&
        String(this.user.role).toLowerCase() === 'group' &&
        this.insurances.length === 1 &&
        this.groups.length === 1
      ) {
        this.groupChoice = this.user.group;
      }

      if (this.groupChoice.length > 0) {
        this.providers = [];
        this.provChoice = [];
        this.locations = [];
        this.locChoice = [];
        this.groupNum = [];

        // Check for pending request and if so cancel old one for new incoming
        this.cancelSearch();
        // Set the CancelToken
        this.cancelSource = axios.CancelToken.source();

        const response = await axios
          .get(`api/selection/${this.insChoice}/group/${this.groupChoice}`, {
            cancelToken: this.cancelSource.token,
          })
          .then((response) => {
            // Set cancelsource back to null
            this.cancelSource = null;
            const { data } = response;
            this.groupObject = data;
            data.forEach((element) => {
              if (this.user.role === 'Provider') {
                if (this.user.provider.includes(element.Provide_Name)) {
                  this.providers.push(element);
                  this.provChoice.push(element);
                }
              } else {
                this.providers.push(element);
              }
              if (this.insChoice) {
                if (this.groupNum.indexOf(element.Center_PCP_Number) === -1) {
                  this.groupNum.push(element.Center_PCP_Number);
                }
              }
            });
          });
      } else {
        this.providers = [];
        this.provChoice = [];
        this.locChoice = [];
        this.locations = [];
        this.groupNum = [];
      }
    },
    async provChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }

      if (this.provChoice !== []) {
        const prov = this.provChoice;
        let allProviders = this.providers;
        let providerNames = [];
        this.locations = [];
        this.locChoice = [];
        this.provNums = [];

        // Create an Array of selected provider names
        prov.forEach((provs) => {
          providerNames.push(provs.Provide_Name);
        });
        // Loop through all the providers objects and see if there is a match with any names in providerNames array
        allProviders.forEach((p) => {
          if (providerNames.indexOf(p.Provide_Name) !== -1) {
            this.provNums.push(p.Center_PCP_Number);
            // Add the location
            if (p.Center_Location === null) {
              this.locations.push(`${p.Center_PCP_Number} - No Location Provided`);
            } else {
              // Add the location and add PCP_IDs, there can be multiple for a single provider
              this.locations.push(p.Center_Location);
            }
          }
        });
      } else {
        this.locChoice = [];
        this.locations = [];
      }
    },
    async insChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
      this.groupChoice = [];
      this.provChoice = [];
      this.locChoice = [];

      const r =
        this.user && this.user.role ? String(this.user.role).toLowerCase() : '';

      if (r === 'provider') {
        const ins = this.user.insurance;
        this.insurances = Array.isArray(ins) ? ins : ins != null && ins !== '' ? [ins] : [];
        if (this.insChoice) {
          await this.getGroups();
        }
        this.groupChoice = this.user.group;
        return;
      }

      if (this.insChoice) {
        await this.getGroups();
      }
    },
    async tab() {
      if (this.showReport) {
        this.changeDetected = true;
      }
      if (this.tab === 1) {
        this.endDate = new Date(new Date().getFullYear(), new Date().getMonth() - 2)
          .toISOString()
          .substr(0, 7);
      } else {
        this.endDate = new Date(new Date().getFullYear(), new Date().getMonth() - 1)
          .toISOString()
          .substr(0, 7);
      }
    },
    async startDate() {
      if (this.showReport === true) {
        this.changeDetected = true;
      }
    },
    async endDate() {
      if (this.showReport === true) {
        this.changeDetected = true;
      }
    },
    async locChoice() {
      if (this.showReport === true) {
        this.changeDetected = true;
      }
    },
  },
  computed: {
    userRoleLower() {
      return this.user && this.user.role ? String(this.user.role).toLowerCase() : '';
    },
    orgDisabled() {
      if (this.userRoleLower === 'provider' || this.userRoleLower === 'group') {
        return true;
      }
      return false;
    },
    insDisabled() {
      if (this.userRoleLower === 'provider' || this.insurances.length == 1) {
        return true;
      }
      return false;
    },
    provDisabled() {
      if (this.groupChoice.length === 0) {
        return true;
      }
      if (this.userRoleLower === 'provider') {
        return true;
      }
      return false;
    },
    locDisabled() {
      if (
        this.provChoice.length === 0 ||
        this.groupChoice.length === 0 ||
        this.insChoice === 'Humana'
      ) {
        return true;
      }
      return false;
    },
    groupDisabled() {
      // Grupos vêm de `api/list/:insurance/groups` — não dependem da organização.
      // Exigir org aqui desativava o select e impedia Comparative KPI sem org escolhida.
      if (!this.insChoice) {
        return true;
      }
      if (this.userRoleLower === 'provider') {
        return true;
      }
      if (this.userRoleLower === 'group' && this.groups.length === 1) {
        this.groupChoice = this.user.group;
        return true;
      }
      return false;
    },
    getAllowedDates() {
      if (this.tab === 1) {
        return new Date(new Date().getFullYear(), new Date().getMonth() - 2)
          .toISOString()
          .substr(0, 7);
      }
      return new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7);
    },
  },
};
</script>
<style scoped>
.newSelect .v-select__selection {
  white-space: nowrap;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selection-text {
  margin-right: 5px;
}
@media only screen and (min-width: 1200px) {
  .selection-text {
    white-space: nowrap;
    overflow: hidden;
    width: 70%;
  }
}
</style>
